import { useTranslation } from "react-i18next";
import { SelectField, TextField } from "@/shared/ui/core";
import { RepeatableList, TextAreaField } from "@/features/admin/components";
import { EditorialBlock, type EditorialBlockLocaleProps } from "./EditorialBlock";
import { EditorialIconField } from "./EditorialIconField";
import { useBlockFilled } from "./useBlockFilled";
import { useEditorialThemeOptions } from "../../productOptions";

/**
 * Bloque 5 · Los ingredientes, uno por uno.
 *
 * El `spec` es el renglón pequeño en mayúsculas que va sobre el nombre
 * —"COENZIMA DE LA ENERGÍA CELULAR"—. Se escribe en minúsculas y con
 * acentos normales: quien lo pone en mayúsculas a mano se queda sin
 * acentos y con un texto que un lector de pantalla deletrea letra a
 * letra. Las mayúsculas las pone la ficha, con CSS.
 */
export function EditorialIngredientsBlock({ locale }: EditorialBlockLocaleProps) {
  const { t } = useTranslation();
  const name = `editorial.${locale}.ingredients`;
  const filled = useBlockFilled(name);
  const themeOptions = useEditorialThemeOptions();

  return (
    <EditorialBlock
      name={name}
      title={t("Ingredientes")}
      description={t("Qué lleva dentro y para qué sirve cada cosa.")}
      filled={filled}
    >
      <TextField
        name={`${name}.title`}
        label={t("Título del bloque")}
        placeholder={t("Qué lleva dentro")}
      />
      <SelectField
        name={`${name}.theme`}
        label={t("Tema")}
        options={themeOptions}
        placeholder={t("El de la ficha")}
        hint={t("Cómo se pinta el bloque: sobre claro o sobre oscuro.")}
      />
      <TextAreaField
        name={`${name}.intro`}
        label={t("Entradilla")}
        rows={2}
        fieldClassName="sm:col-span-2"
      />

      <div className="sm:col-span-2">
        <RepeatableList
          name={`${name}.items`}
          max={12}
          addLabel={t("Añadir ingrediente")}
          itemLabel={(index) => t("Ingrediente {{n}}", { n: index + 1 })}
          emptyHint={t("Todavía no hay ingredientes. Añade el primero.")}
          newItem={() => ({ icon: "", name: "", spec: "", description: "" })}
        >
          {({ name: row }) => (
            <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
              <TextField
                name={`${row}.name`}
                label={t("Nombre del ingrediente")}
                placeholder="Coenzima Q10"
              />
              <EditorialIconField name={`${row}.icon`} />
              <TextField
                name={`${row}.spec`}
                label={t("Renglón corto")}
                placeholder={t("Coenzima de la energía celular")}
                hint={t("Escríbelo normal: las mayúsculas las pone la ficha.")}
                fieldClassName="sm:col-span-2"
              />
              <TextAreaField
                name={`${row}.description`}
                label={t("Descripción")}
                rows={3}
                fieldClassName="sm:col-span-2"
              />
            </div>
          )}
        </RepeatableList>
      </div>
    </EditorialBlock>
  );
}
